import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Eye, MoreHorizontal, Plus, Search, Trash2 } from 'lucide-react';
import { api, baixarArquivo, ErroApi } from '../../api/cliente';
import type { Job } from '../../api/tipos';
import {
  Aviso,
  Botao,
  BotaoLink,
  Cartao,
  Celula,
  Esqueleto,
  Etiqueta,
  ItemMenu,
  Menu,
  Progresso,
  Tabela,
  Td,
  Th,
  useConfirmar,
  useNotificar,
  Vazio,
} from '../../componentes/ui';
import { Pagina, TituloPagina } from '../../componentes/Pagina';
import { dataHora, numero, plural, porcentagem } from '../../util/formato';
import { etiquetaDoJob, terminou } from './statusJob';
import css from './Downloads.module.css';

const INTERVALO_ATIVO_MS = 5_000;

export function Downloads() {
  const navegar = useNavigate();
  const confirmar = useConfirmar();
  const notificar = useNotificar();
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [baixando, setBaixando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const lista = await api<Job[]>('/nfe/lote');
    setJobs(lista);
    return lista;
  }, []);

  useEffect(() => {
    let vivo = true;
    let timer: number | undefined;

    async function ciclo() {
      try {
        const lista = await carregar();
        if (!vivo) return;
        // Só continua pesquisando enquanto houver job em andamento — parado,
        // bater na API de 5 em 5 s não traz informação nova.
        if (lista.some((j) => !terminou(j))) {
          timer = window.setTimeout(() => void ciclo(), INTERVALO_ATIVO_MS);
        }
      } catch (err) {
        if (vivo) setErro(err instanceof ErroApi ? err.message : 'Falha ao carregar as buscas.');
      }
    }

    void ciclo();
    return () => {
      vivo = false;
      if (timer) clearTimeout(timer);
    };
  }, [carregar]);

  async function baixar(job: Job) {
    setBaixando(job.id);
    try {
      await baixarArquivo(`/nfe/lote/${job.id}/download`, `nfe-lote-${job.id}.zip`);
    } catch (err) {
      notificar(err instanceof ErroApi ? err.message : 'Não foi possível baixar o ZIP agora.', 'erro');
    } finally {
      setBaixando(null);
    }
  }

  async function remover(job: Job) {
    const emAndamento = !terminou(job);
    const ok = await confirmar({
      titulo: emAndamento ? 'Cancelar esta busca?' : 'Apagar esta busca?',
      mensagem: emAndamento
        ? `A busca para de rodar e o que já foi baixado (${plural(job.ok, 'XML', 'XMLs')}) é apagado. O saldo das chaves que faltam volta para a sua conta.`
        : `Os XMLs desta busca (${plural(job.ok, 'arquivo')}) são apagados do servidor. Se ainda não baixou o ZIP, faça isso antes.`,
      confirmar: emAndamento ? 'Cancelar busca' : 'Apagar busca',
      cancelar: 'Voltar',
      perigo: true,
    });
    if (!ok) return;

    try {
      await api<void>(`/nfe/lote/${job.id}`, { metodo: 'DELETE' });
      notificar(emAndamento ? 'Busca cancelada.' : 'Busca apagada.');
      await carregar();
    } catch (err) {
      notificar(err instanceof ErroApi ? err.message : 'Não foi possível remover a busca.', 'erro');
    }
  }

  return (
    <Pagina>
      <TituloPagina
        titulo="Downloads"
        descricao="Suas buscas por chave de acesso e os ZIPs prontos para baixar."
        acoes={
          <BotaoLink to="/painel/nova-busca" icone={<Plus />}>
            Nova busca
          </BotaoLink>
        }
      />

      {erro && <Aviso>{erro}</Aviso>}

      {jobs === null ? (
        <Cartao padding={0}>
          <div className={css.carregando}>
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
          </div>
        </Cartao>
      ) : jobs.length === 0 ? (
        <Cartao padding={0}>
          <Vazio
            icone={<Search />}
            titulo="Nenhuma busca ainda"
            descricao="Cole as chaves de acesso ou envie um CSV. O progresso e o ZIP aparecem aqui."
            acao={
              <BotaoLink to="/painel/nova-busca" icone={<Plus />}>
                Começar uma busca
              </BotaoLink>
            }
          />
        </Cartao>
      ) : (
        <>
          <Cartao padding={0}>
            <Tabela rotulo="Buscas">
              <thead>
                <tr>
                  <Th>Busca</Th>
                  <Th>Situação</Th>
                  <Th>Progresso</Th>
                  <Th>Resultado</Th>
                  <Th encolher>
                    <span className="sr-only">Ações</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => {
                  const processados = job.ok + job.notfound + job.erro;
                  const progresso = job.total > 0 ? processados / job.total : 0;
                  const etiqueta = etiquetaDoJob(job);
                  return (
                    <tr key={job.id}>
                      <Td>
                        <Celula
                          para={`/painel/downloads/${job.id}`}
                          principal={plural(job.total, 'chave')}
                          secundaria={`Criada em ${dataHora(job.createdAt)}`}
                        />
                      </Td>
                      <Td>
                        <Etiqueta tom={etiqueta.tom} pulsando={etiqueta.pulsando}>
                          {etiqueta.texto}
                        </Etiqueta>
                      </Td>
                      <Td>
                        <div className={css.progresso}>
                          <Progresso
                            valor={progresso}
                            rotulo={`Progresso da busca de ${plural(job.total, 'chave')}`}
                            tom={job.erro > 0 ? 'erro' : undefined}
                          />
                          <span className={`${css.percentual} num`}>{porcentagem(progresso)}</span>
                        </div>
                      </Td>
                      <Td>
                        <div className={`${css.numeros} num`}>
                          <span className={css.baixados}>{plural(job.ok, 'baixado')}</span>
                          {job.notfound > 0 && (
                            <span>{plural(job.notfound, 'não encontrada', 'não encontradas')}</span>
                          )}
                          {job.erro > 0 && (
                            <span className={css.falhas}>{numero(job.erro)} com erro</span>
                          )}
                          {!terminou(job) && (
                            <span>{numero(Math.max(0, job.total - processados))} na fila</span>
                          )}
                        </div>
                      </Td>
                      <Td>
                        <div className={css.acoes}>
                          <Botao
                            aparencia="secundario"
                            pequeno
                            icone={<Download />}
                            onClick={() => void baixar(job)}
                            carregando={baixando === job.id}
                            disabled={job.ok === 0}
                            title={job.ok === 0 ? 'Nenhum XML baixado ainda' : 'Baixa os XMLs obtidos até agora'}
                          >
                            ZIP
                          </Botao>
                          <Menu
                            gatilho={({ alternar, ...aria }) => (
                              <Botao
                                aparencia="discreto"
                                pequeno
                                soIcone
                                icone={<MoreHorizontal />}
                                aria-label="Mais ações"
                                onClick={alternar}
                                {...aria}
                              />
                            )}
                          >
                            <ItemMenu icone={<Eye />} onClick={() => navegar(`/painel/downloads/${job.id}`)}>
                              Ver detalhes
                            </ItemMenu>
                            <ItemMenu icone={<Trash2 />} perigo onClick={() => void remover(job)}>
                              {terminou(job) ? 'Apagar busca' : 'Cancelar busca'}
                            </ItemMenu>
                          </Menu>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Tabela>
          </Cartao>
          <Aviso tom="info">
            Buscas finalizadas são apagadas automaticamente depois de 48 horas, junto com os XMLs. Baixe o
            ZIP dentro desse prazo.
          </Aviso>
        </>
      )}
    </Pagina>
  );
}
